import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { START_NODE_ID } from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import { MAX_LABEL_LENGTH, ELLIPSIS } from './ellipsis.tsx';
import { NODE_TYPE, EDGE_TYPE } from './directionKeys.tsx';
import {
	type Size,
	type Point,
	type PortInfo,
	type DiagramTransition,
	type DiagramStatus,
	type PortIndex,
	toPortKey,
	type DiagramDirectTransitionWithDefinedPorts,
	type DiagramSharedTransitionWithDefinedPorts,
	type ConnectionPorts,
} from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import type { OverlapPlacement } from './getLabelPlacement.tsx';
import type {
	ReroutingTransition,
	ReroutingTransitionValid,
} from './ReroutingTransitionValid.types.tsx';

export const isStartNodeReference = (reference: ItemReference | undefined): boolean => {
	if (!reference || reference.type !== NODE_TYPE) {
		return false;
	}

	return START_NODE_ID === reference.id;
};

export const statusMatchesReference = (
	status: DiagramStatus,
	reference: ItemReference | undefined,
): boolean => {
	if (!reference || reference.type !== NODE_TYPE) {
		return false;
	}

	return status.id === reference.id;
};

export const transitionMatchesReference = (
	transition: DiagramTransition,
	reference: ItemReference | undefined,
): boolean => {
	if (!reference || reference.type !== EDGE_TYPE) {
		return false;
	}

	return transition.id === reference.id;
};

const isTransitionConnectedToStatus = (transition: DiagramTransition, statusId: string) => {
	if (transition.type === 'global-looped') {
		return false;
	}

	if (transition.target === statusId) {
		return true;
	}

	if (transition.type === 'direct' && transition.source === statusId) {
		return true;
	}

	if (transition.type === 'shared' && transition.links.some((link) => link.source === statusId)) {
		return true;
	}

	return false;
};

export const isHighlightedTransition = (
	transition: DiagramTransition,
	selectedItem: ItemReference | undefined,
	hoveredItem: ItemReference | undefined,
): boolean => {
	if (hoveredItem && hoveredItem.type === NODE_TYPE) {
		return isTransitionConnectedToStatus(transition, hoveredItem.id);
	}

	if (selectedItem && selectedItem.type === NODE_TYPE) {
		return isTransitionConnectedToStatus(transition, selectedItem.id);
	}

	return false;
};

export const findPort = (
	ports: Record<string, PortInfo>,
	source: string,
	sourceIndex: PortIndex | undefined,
): PortInfo | undefined => {
	if (sourceIndex === undefined) {
		return undefined;
	}

	if (source === START_NODE_ID) {
		return ports[START_NODE_ID];
	}

	return ports[toPortKey(source, sourceIndex)];
};

export const findAllPorts = (
	ports: Record<string, PortInfo>,
	statusPortPairs: { statusId: string; portIndex?: PortIndex }[],
): PortInfo[] | undefined => {
	if (!isEveryPropertyDefined(statusPortPairs, 'portIndex')) {
		return undefined;
	}

	return compact(
		statusPortPairs.map(({ statusId, portIndex }) => ports[toPortKey(statusId, portIndex)]),
	);
};

export const isTransitionRerouting = (
	transition: DiagramTransition,
	reroutingTransition: ReroutingTransition,
): boolean =>
	reroutingTransition.stage !== 'none' && reroutingTransition?.transition.id === transition.id;

export const isPortMovingForReroutingTransition = (
	port: PortInfo | undefined,
	side: 'source' | 'target',
	transitionId: string,
	reroutingTransition: ReroutingTransition,
): reroutingTransition is ReroutingTransitionValid =>
	port !== undefined &&
	reroutingTransition.stage !== 'none' &&
	reroutingTransition?.transition.id === transitionId &&
	reroutingTransition.side === side &&
	port.key === reroutingTransition.originalPortKey;

export const updateTransitionPortIfRerouting = (
	port: PortInfo | undefined,
	side: 'source' | 'target',
	transition: DiagramTransition,
	reroutingTransition: ReroutingTransition,
): PortInfo | undefined => {
	if (
		!port ||
		reroutingTransition.stage === 'none' ||
		reroutingTransition?.transition.id !== transition.id ||
		reroutingTransition.side !== side ||
		port.key !== reroutingTransition.originalPortKey
	) {
		return port;
	}

	return { ...port, position: reroutingTransition.currentPosition };
};

export const calculateViewBox = (
	diagramSize: Size,
	pan: Point,
	zoom: number,
): {
	width: number;
	height: number;
	top: number;
	left: number;
} => {
	const viewBoxWidth = diagramSize.width / zoom;
	const viewBoxHeight = diagramSize.height / zoom;

	return {
		width: viewBoxWidth,
		height: viewBoxHeight,
		top: pan.y - viewBoxHeight / 2,
		left: pan.x - viewBoxWidth / 2,
	};
};

export const truncateLabel = (text: string, maxLength: number = MAX_LABEL_LENGTH): string =>
	text.length <= maxLength ? text : text.substring(0, maxLength - ELLIPSIS.length) + ELLIPSIS;

/** Mockable function to getBBox which is not available in tests */
export function getBBox(element: SVGGraphicsElement): false | DOMRect {
	return 'getBBox' in element && typeof element.getBBox === 'function' && element.getBBox();
}

/** Mockable function for element.getBoundingClientRect which is not available in tests */
export function getBoundingClientRect(element: HTMLElement): DOMRect {
	return element.getBoundingClientRect();
}

/** Checks that transition is either direct or shared, and all ports are defined.
 *  If `true` the type is narrowed to DiagramDirectTransitionWithDefinedPorts or DiagramSharedTransitionWithDefinedPorts
 */
export function isDirectOrSharedTransitionWithDefinedPorts(
	transition: DiagramTransition,
): transition is DiagramDirectTransitionWithDefinedPorts | DiagramSharedTransitionWithDefinedPorts {
	if (transition.type === 'global') return false;

	if (transition.type === 'global-looped') return false;

	if (transition.type === 'direct') {
		return transition.sourceIndex !== undefined && transition.targetIndex !== undefined;
	}

	// else shared
	return transition.links.every(
		(link) => link.sourceIndex !== undefined && link.targetIndex !== undefined,
	);
}

// -? makes the property `P` of object `T` _not_ optional
type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

/** Guard logic to ensure no properties in collection are undefined, and lets typescript know as well */
export function isEveryPropertyDefined<T, K extends keyof T>(
	collection: T[],
	...properties: K[]
): collection is WithRequired<T, K>[] {
	return collection.every((item) => properties.every((property) => item[property] !== undefined));
}

export const isTransitionOverlapping = (
	transitionId: string,
	transitionLabelPlacements: OverlapPlacement[],
): boolean => transitionLabelPlacements.some((x) => x.transitionId === transitionId);

export const isAnyOverlappingTransitionHovered = (
	transitionId: string,
	hoveredTransitionId: string | undefined,
	transitionLabelPlacements: OverlapPlacement[],
): boolean => {
	if (!hoveredTransitionId) {
		return false;
	}

	// find overlap info if transition overlaps another
	const overlapInfo = transitionLabelPlacements.find((x) => x.transitionId === transitionId);

	if (!overlapInfo) {
		return false;
	}

	// check if hovered item overlaps given transition
	return transitionLabelPlacements.some(
		(x) => x.transitionId === hoveredTransitionId && x.overlapKey === overlapInfo.overlapKey,
	);
};

export const isNilOrEmpty = <T extends object>(
	value: T | null | undefined,
): value is null | undefined => {
	return isNil(value) || isEmpty(value);
};

export const areConnectionsEqual = (
	conn1: ConnectionPorts | undefined,
	conn2: ConnectionPorts | undefined,
): boolean => {
	if (!conn1 || !conn2) return false;

	// Check if connections match in either direction (A→B or B→A)
	const forwardMatch =
		conn1.sourcePort?.portIndex === conn2.sourcePort?.portIndex &&
		conn1.sourcePort?.statusId === conn2.sourcePort?.statusId &&
		conn1.targetPort?.portIndex === conn2.targetPort?.portIndex &&
		conn1.targetPort?.statusId === conn2.targetPort?.statusId;

	const reverseMatch =
		conn1.sourcePort?.portIndex === conn2.targetPort?.portIndex &&
		conn1.sourcePort?.statusId === conn2.targetPort?.statusId &&
		conn1.targetPort?.portIndex === conn2.sourcePort?.portIndex &&
		conn1.targetPort?.statusId === conn2.sourcePort?.statusId;

	return forwardMatch || reverseMatch;
};
